import { DRAFTS, TARGETS } from '../data/candidates.ts';
import {
  type FormationCode,
  FORMATIONS,
  localizedCode,
  positionsByWeight,
  WEIGHT_MAX,
  WEIGHT_SECONDARY,
} from '../data/positions.ts';
import { initialSquad, ROSTER, toPlayer } from '../data/squad.ts';
import { buildBoard, datasetPositionMap, lineupFromBoard, type SlotBoard } from './board.ts';
import { applyDepthPositions, DEPTH_POSITIONS, moveInDepth, resolveDepthOrder } from './depth.ts';
import type {
  Candidate,
  DepthOrder,
  Filters,
  NewPlayerForm,
  Player,
  PositionCode,
  SortDirection,
  SortField,
  SquadStatus,
} from '../types.ts';
import { DEFAULT_WAGE_CAP, money, STATUS_META } from './rules.ts';

/** Slot index → player id. Slots not present in the map are empty. */
export type Lineup = Record<number, string>;

export interface SquadState {
  players: Player[];
  /** Id of the player shown in the right-hand card. */
  selectedId: string;
  formation: FormationCode;
  lineup: Lineup;
  /** Annual wage budget in M€. */
  wageCap: number;
  /** Player currently being dragged, so drop targets can highlight themselves. */
  draggingId: string | null;
  sortField: SortField;
  sortDirection: SortDirection;
  rulesPanelOpen: boolean;
  playerPanelOpen: boolean;
  transferModalOpen: boolean;
  posterOpen: boolean;
  /** Named transfer targets, fixed. */
  targets: Candidate[];
  /** Anonymous profile drafts; the user can add to these. */
  drafts: Candidate[];
  /** Which candidate is highlighted as applied, e.g. "t2" or "d0". */
  activeCandidateKey: string | null;
  filters: Filters;
  form: NewPlayerForm;
  /** Counter behind generated ids and shirt numbers for simulated signings. */
  nextId: number;
  /** The user's hand-arranged depth chart. `null` means follow the dataset. */
  depthOrder: DepthOrder | null;
}

export const EMPTY_FILTERS: Filters = {
  query: '',
  position: '',
  age: '',
  nationality: '',
  homegrown: '',
  status: '',
};

export const EMPTY_FORM: NewPlayerForm = {
  name: '',
  birthYear: 2004,
  nationality: 'TUR',
  marketValue: 15,
  wage: 2.5,
  rating: 7.1,
  homegrown: 'yok',
  positionWeights: { AM: WEIGHT_MAX },
  turkeyEligible: true,
};

const DEFAULT_FORMATION: FormationCode = '4-2-3-1';

export function initialState(): SquadState {
  const players = initialSquad();
  const selected = players.find((p) => p.name === 'Victor Osimhen') ?? players[0];
  const opening = buildBoard(
    DEFAULT_FORMATION,
    players.filter((p) => p.availability === 'A'),
    resolveDepthOrder(players, null),
    datasetPositionMap(players),
    true,
  );
  return {
    players,
    selectedId: selected.id,
    formation: DEFAULT_FORMATION,
    lineup: lineupFromBoard(opening),
    wageCap: DEFAULT_WAGE_CAP,
    draggingId: null,
    sortField: 'marketValue',
    sortDirection: -1,
    rulesPanelOpen: true,
    playerPanelOpen: true,
    transferModalOpen: false,
    posterOpen: false,
    targets: TARGETS,
    drafts: DRAFTS,
    activeCandidateKey: null,
    filters: EMPTY_FILTERS,
    form: EMPTY_FORM,
    nextId: 100,
    depthOrder: null,
  };
}

export type Action =
  | { type: 'hydrate'; state: Partial<SquadState> }
  | { type: 'selectPlayer'; id: string }
  | { type: 'dragStart'; id: string }
  | { type: 'dragEnd' }
  | { type: 'setStatus'; id: string; status: SquadStatus }
  | { type: 'removePlayer'; id: string }
  | { type: 'addFormPlayer' }
  | { type: 'setFormation'; formation: FormationCode }
  | { type: 'assignSlot'; slot: number; id: string }
  | { type: 'clearSlot'; slot: number }
  | { type: 'clearLineup' }
  | { type: 'autoFillLineup' }
  | { type: 'sortBy'; field: SortField }
  | { type: 'setFilter'; field: keyof Filters; value: string }
  | { type: 'resetFilters' }
  | { type: 'setWageCap'; value: number }
  | { type: 'togglePanel'; panel: 'rules' | 'player' }
  | { type: 'setTransferModal'; open: boolean }
  | { type: 'setPoster'; open: boolean }
  | { type: 'patchForm'; patch: Partial<NewPlayerForm> }
  | { type: 'cycleFormPosition'; position: PositionCode }
  | { type: 'toggleTurkeyEligible' }
  | { type: 'resetForm' }
  | { type: 'applyCandidate'; candidate: Candidate; key: string; withName: boolean }
  | { type: 'saveDraft' }
  | { type: 'moveInDepth'; id: string; position: PositionCode; beforeId: string | null }
  | { type: 'resetDepth' };

/**
 * The squad as the user has arranged it: dataset players with the depth chart's
 * position applied. Everything that ranks players by position reads this, so a chip
 * dragged in the depth chart really does change where the player is picked.
 */
export function effectivePlayers(state: SquadState): Player[] {
  return applyDepthPositions(state.players, resolveDepthOrder(state.players, state.depthOrder));
}

/** The squad laid out over a formation — what the pitch, chart and poster all read. */
export function boardFor(state: SquadState, formation: FormationCode): SlotBoard {
  const players = effectivePlayers(state);
  return buildBoard(
    formation,
    players.filter((p) => p.availability === 'A'),
    resolveDepthOrder(state.players, state.depthOrder),
    datasetPositionMap(state.players),
    state.depthOrder === null, // untouchedChart
  );
}

/** Sort fields that read best ascending on first click. */
const ASCENDING_FIRST: SortField[] = ['name', 'age'];

export function reducer(state: SquadState, action: Action): SquadState {
  switch (action.type) {
    case 'hydrate':
      return { ...state, ...action.state };

    case 'selectPlayer':
      return { ...state, selectedId: action.id };

    case 'dragStart':
      return { ...state, draggingId: action.id, selectedId: action.id };

    case 'dragEnd':
      return { ...state, draggingId: null };

    case 'setStatus': {
      const meta = STATUS_META[action.status];
      return {
        ...state,
        players: state.players.map((p) =>
          p.id === action.id
            ? {
                ...p,
                status: action.status,
                availability: meta.availability,
                onListA: meta.onListA,
              }
            : p,
        ),
      };
    }

    case 'removePlayer': {
      const players = state.players.filter((p) => p.id !== action.id);
      const lineup: Lineup = {};
      for (const key of Object.keys(state.lineup)) {
        const slot = Number(key);
        if (state.lineup[slot] !== action.id) lineup[slot] = state.lineup[slot];
      }
      return {
        ...state,
        players,
        lineup,
        selectedId: state.selectedId === action.id ? (players[0]?.id ?? '') : state.selectedId,
      };
    }

    case 'addFormPlayer': {
      const form = state.form;
      // Name and at least one position are required; otherwise the action is a no-op.
      const positions = positionsByWeight(form.positionWeights);
      if (!form.name.trim() || !positions.length) return state;
      const player: Player = {
        id: 'n' + state.nextId,
        shirtNo: (state.nextId % 90) + 10,
        name: form.name.trim(),
        nationality: form.nationality || 'TUR',
        birthYear: form.birthYear,
        positionWeights: { ...form.positionWeights },
        positions,
        excludedPositions: [],
        marketValue: form.marketValue,
        wage: form.wage,
        contractUntil: 2030,
        appearances: 0,
        minutes: 0,
        goals: 0,
        assists: 0,
        rating: form.rating,
        homegrown: form.homegrown,
        turkeyEligible: form.turkeyEligible,
        preferredSide: 'any',
        status: 'A',
        availability: 'A',
        onListA: true,
        isNew: true,
        form: [6.5, 6.5, 6.5, 6.5, 6.5],
      };
      return {
        ...state,
        players: state.players.concat([player]),
        nextId: state.nextId + 1,
        selectedId: player.id,
        transferModalOpen: false,
        playerPanelOpen: true,
      };
    }

    case 'setFormation':
      return {
        ...state,
        formation: action.formation,
        lineup: lineupFromBoard(boardFor(state, action.formation)),
      };

    case 'assignSlot': {
      const lineup: Lineup = { ...state.lineup };
      if (lineup[action.slot] === action.id) return { ...state, draggingId: null };
      // A player can only hold one slot — clear any previous one first.
      for (const key of Object.keys(lineup)) {
        const slot = Number(key);
        if (lineup[slot] === action.id) delete lineup[slot];
      }
      lineup[action.slot] = action.id;

      // Putting a player on the pitch is a statement about where he belongs, so it
      // re-files him at the head of that position in the depth chart. Without this the
      // two panels drift apart: the pitch would show him at centre-back while the chart
      // still had him down as a winger, and the next EN İYİ 11 would undo the move.
      const code = FORMATIONS[state.formation][action.slot]?.[0];
      let depthOrder = state.depthOrder;
      if (code) {
        const resolved = resolveDepthOrder(state.players, state.depthOrder);
        const others = (resolved[code] ?? []).filter((x) => x !== action.id);
        depthOrder = moveInDepth(resolved, action.id, code, others[0] ?? null);
      }

      return { ...state, lineup, draggingId: null, depthOrder };
    }

    case 'clearSlot': {
      const lineup: Lineup = { ...state.lineup };
      delete lineup[action.slot];
      return { ...state, lineup };
    }

    case 'clearLineup':
      return { ...state, lineup: {} };

    case 'autoFillLineup':
      return { ...state, lineup: lineupFromBoard(boardFor(state, state.formation)) };

    case 'sortBy':
      if (state.sortField === action.field) {
        return { ...state, sortDirection: (-state.sortDirection as SortDirection) };
      }
      return {
        ...state,
        sortField: action.field,
        sortDirection: ASCENDING_FIRST.indexOf(action.field) >= 0 ? 1 : -1,
      };

    case 'setFilter':
      return { ...state, filters: { ...state.filters, [action.field]: action.value } };

    case 'resetFilters':
      return { ...state, filters: EMPTY_FILTERS };

    case 'setWageCap':
      return { ...state, wageCap: action.value };

    case 'togglePanel':
      return action.panel === 'rules'
        ? { ...state, rulesPanelOpen: !state.rulesPanelOpen }
        : { ...state, playerPanelOpen: !state.playerPanelOpen };

    case 'setTransferModal':
      return { ...state, transferModalOpen: action.open };

    case 'setPoster':
      return { ...state, posterOpen: action.open };

    case 'patchForm':
      return { ...state, form: { ...state.form, ...action.patch } };

    // Clicking a position cycles its weight: off → 5 → 2 → off. A candidate profile can
    // carry weights between those two, and one click brings such a position down to 2.
    case 'cycleFormPosition': {
      const positionWeights = { ...state.form.positionWeights };
      const current = positionWeights[action.position] ?? 0;
      if (current === 0) positionWeights[action.position] = WEIGHT_MAX;
      else if (current > WEIGHT_SECONDARY) positionWeights[action.position] = WEIGHT_SECONDARY;
      else delete positionWeights[action.position];
      return { ...state, form: { ...state.form, positionWeights } };
    }

    case 'toggleTurkeyEligible':
      return {
        ...state,
        form: { ...state.form, turkeyEligible: !state.form.turkeyEligible },
      };

    case 'resetForm':
      return { ...state, form: EMPTY_FORM };

    case 'applyCandidate':
      return {
        ...state,
        activeCandidateKey: action.key,
        form: {
          name: action.withName ? action.candidate.name : '',
          birthYear: action.candidate.birthYear,
          nationality: action.candidate.nationality,
          marketValue: action.candidate.marketValue,
          wage: action.candidate.wage,
          rating: action.candidate.rating,
          homegrown: action.candidate.homegrown,
          positionWeights: { ...action.candidate.positionWeights },
          turkeyEligible: action.candidate.turkeyEligible,
        },
      };

    case 'saveDraft': {
      const form = state.form;
      // The draft label is user-facing, so it prints the localized codes (OOS/SLK),
      // never the internal English ones.
      const name =
        positionsByWeight(form.positionWeights).map(localizedCode).join('/') +
        ' · ' +
        form.birthYear +
        ' doğumlu' +
        (form.turkeyEligible ? ' yerli' : ' yabancı');
      const draft: Candidate = {
        name,
        summary:
          money(form.marketValue) +
          ' değer · ' +
          money(form.wage) +
          ' maaş · reyting ' +
          form.rating,
        birthYear: form.birthYear,
        nationality: form.nationality,
        positionWeights: { ...form.positionWeights },
        marketValue: form.marketValue,
        wage: form.wage,
        rating: form.rating,
        homegrown: form.homegrown,
        turkeyEligible: form.turkeyEligible,
      };
      return { ...state, drafts: [draft].concat(state.drafts), activeCandidateKey: 'd0' };
    }

    case 'moveInDepth': {
      const resolved = resolveDepthOrder(state.players, state.depthOrder);
      const from = DEPTH_POSITIONS.find((code) => (resolved[code] ?? []).indexOf(action.id) >= 0);
      const next = {
        ...state,
        depthOrder: moveInDepth(resolved, action.id, action.position, action.beforeId),
      };

      // Re-seat the pitch, but only the slots this move can have changed. Refreshing
      // everything would refill slots the user deliberately emptied; refreshing nothing
      // would leave the pitch showing an order the chart no longer agrees with.
      const touched = new Set<PositionCode>([action.position]);
      if (from) touched.add(from);
      const slots = FORMATIONS[state.formation];
      const board = boardFor(next, state.formation);
      const lineup: Lineup = {};
      for (const key of Object.keys(state.lineup)) {
        const slot = Number(key);
        if (state.lineup[slot] !== action.id) lineup[slot] = state.lineup[slot];
      }
      slots.forEach((slot, index) => {
        if (!touched.has(slot[0])) return;
        const head = board.queues[index][0];
        if (head) lineup[index] = head.id;
        else delete lineup[index];
      });

      return { ...next, lineup };
    }

    case 'resetDepth': {
      // The whole chart goes back to the dataset, so the pitch has to follow it all the
      // way — unlike a single drag, there is no edit left worth preserving.
      const next = { ...state, depthOrder: null };
      return { ...next, lineup: lineupFromBoard(boardFor(next, state.formation)) };
    }

    default:
      return state;
  }
}

/* ------------------------------------------------------------------ *
 * Persistence
 * ------------------------------------------------------------------ */

export const STORAGE_KEY = 'gskadro.plan.v4';
const STORAGE_VERSION = 4;

/** The slice of state written to localStorage. Targets are static, so they are skipped. */
interface StoredPlan {
  v: number;
  players: Player[];
  lineup: Lineup;
  formation: FormationCode;
  wageCap: number;
  drafts: Candidate[];
  selectedId: string;
  nextId: number;
  sortField: SortField;
  sortDirection: SortDirection;
  rulesPanelOpen: boolean;
  playerPanelOpen: boolean;
  depthOrder: DepthOrder | null;
}

export function savePlan(state: SquadState): void {
  try {
    const plan: StoredPlan = {
      v: STORAGE_VERSION,
      players: state.players,
      lineup: state.lineup,
      formation: state.formation,
      wageCap: state.wageCap,
      drafts: state.drafts,
      selectedId: state.selectedId,
      nextId: state.nextId,
      sortField: state.sortField,
      sortDirection: state.sortDirection,
      rulesPanelOpen: state.rulesPanelOpen,
      playerPanelOpen: state.playerPanelOpen,
      depthOrder: state.depthOrder,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
  } catch {
    // Private browsing or a full quota — the plan simply is not persisted.
  }
}

/**
 * Reads a saved plan.
 *
 * Only the status dropdown is editable on a roster player, so the saved copy is trusted
 * for exactly that — `status` and the `availability` / `onListA` it implies — plus the
 * `id`, which the lineup references. Every other field is scouting data and is re-read
 * from `data/squad.ts` by name, so correcting a shirt number, a rating or a market value
 * reaches plans that were saved before the correction instead of being shadowed by them.
 * Players added through the simulator are not in the roster and pass through untouched.
 */
export function loadPlan(): Partial<SquadState> | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const plan = JSON.parse(raw) as StoredPlan | null;
    if (!plan || plan.v !== STORAGE_VERSION || !Array.isArray(plan.players)) return null;

    const current = new Map(ROSTER.map((raw, i) => [raw.name, toPlayer(raw, i)]));
    const players = plan.players.map((saved) => {
      const fresh = current.get(saved.name);
      if (!fresh) return saved;
      return {
        ...fresh,
        id: saved.id,
        status: saved.status,
        availability: saved.availability,
        onListA: saved.onListA,
      };
    });

    const restored: Partial<SquadState> = { players };
    if (plan.lineup) restored.lineup = plan.lineup;
    if (plan.formation && plan.formation in FORMATIONS) restored.formation = plan.formation;
    if (plan.wageCap) restored.wageCap = plan.wageCap;
    if (plan.drafts) restored.drafts = plan.drafts;
    if (plan.selectedId) restored.selectedId = plan.selectedId;
    if (plan.nextId) restored.nextId = plan.nextId;
    if (plan.sortField) restored.sortField = plan.sortField;
    if (plan.sortDirection) restored.sortDirection = plan.sortDirection;
    if (plan.rulesPanelOpen !== undefined) restored.rulesPanelOpen = plan.rulesPanelOpen;
    if (plan.playerPanelOpen !== undefined) restored.playerPanelOpen = plan.playerPanelOpen;
    // The chart is the user's own arrangement, so it is trusted as saved.
    if (plan.depthOrder) restored.depthOrder = plan.depthOrder;
    return restored;
  } catch {
    return null;
  }
}

/** Wipes the saved plan and reloads back to the starting squad. */
export function resetPlan(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
  window.location.reload();
}
